import * as React from 'react'

import invariant from 'invariant'
import capitalize from 'lodash/capitalize'
import intersection from 'lodash/intersection'
import isNil from 'lodash/isNil'
import keyBy from 'lodash/keyBy'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'

import {useAppDispatch, useAppSelector} from '../hooks/redux'
import {fatalError} from '../redux/features/core'
import {
  openConfirmDeleteModal,
  openConfirmToggleAvailabilityModal,
  openCreateShippingsModal,
  openEditShippingsModal,
} from '../redux/features/modal'

import useAsync from '../../../commons/src/hooks/use-async'
import useSearch from '../../../commons/src/hooks/use-search'
import useSelectedIds from '../hooks/use-selected-ids'
import useSortAttributes, {SortDefinition} from '../hooks/use-sort-attributes'
import useTableRowFilters, {FilterMap} from '../hooks/use-table-row-filters'
import {formatPrice, formatTimeRange} from '../libs/formatter'
import {RangeMatchingFilter, StringMatchingFilter, filterTableRows} from '../libs/table-row-filter'
import * as shippingApi from '../services/api/shipping'
import {createShippingSearch} from '../services/search'

import {Shipping, ShippingId, ShippingTableRowContent} from '../types'

import deleteIcon from '../../../commons/src/assets/icons/delete.svg'
import editIcon from '../../../commons/src/assets/icons/edit.svg'

import ActionBar from '../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../commons/src/components/button'
import CheckboxField from '../../../commons/src/components/checkbox-field'
import FilterBar from '../../../commons/src/components/filter-bar'
import Headline from '../../../commons/src/components/headline'
import Icon from '../../../commons/src/components/icon'
import LinkButton from '../../../commons/src/components/link-button'
import SearchInputField from '../../../commons/src/components/search-input-field'
import Stack from '../../../commons/src/components/stack'
import ToggleField from '../../../commons/src/components/toggle-field'
import ActionHeaderLayout from '../components/action-header-layout'
import ActionHeader from '../components/action-header-layout/action-header'
import Container from '../components/container'
import PageContentLayout from '../components/page-content-layout'
import PageHeader from '../components/page-content-layout/PageHeader'
import StaggeredValues from '../components/staggered-values/StaggeredValues'
import Table from '../components/table'
import TableCell from '../components/table/TableCell'
import TableHeaderCell from '../components/table/TableHeaderCell'
import TableRow from '../components/table/TableRow'

import RangeMatchingFilterPartial from './filters/range-matching-filter-partial'
import StringMatchingFilterPartial from './filters/string-matching-filter-partial'
import PagePartial from './page-partial'

type TableRowContent = Shipping & {
  regionName: string
  countryName: string
}

const initialFilters: FilterMap<ShippingTableRowContent> = {
  region: new StringMatchingFilter({
    key: 'regionName',
    filterValues: [],
  }),
  country: new StringMatchingFilter({
    key: 'countryCode',
    filterValues: [],
  }),
  type: new StringMatchingFilter({
    key: 'type',
    filterValues: [],
  }),
  deliveryTime: new RangeMatchingFilter({
    minKey: 'deliveryTimeFast',
    maxKey: 'deliveryTimeSlow',
    range: {},
  }),
}

const shippingFilterOptions = [
  {
    id: 'standard',
    label: 'Standard',
    searchable: 'Standard',
  },
  {
    id: 'express',
    label: 'Express',
    searchable: 'Express',
  },
]

const isSinglePrice = (price: Shipping['price']): price is number => typeof price === 'number'

const getPriceForSorting = (price: Shipping['price']): number =>
  isSinglePrice(price) ? price : price[0].price

const SORT_ATTRIBUTES: SortDefinition<TableRowContent> = {
  name: {
    asc: ({name: a}, {name: b}) => a.localeCompare(b),
    desc: ({name: a}, {name: b}) => b.localeCompare(a),
  },
  region: {
    asc: ({regionName: a}, {regionName: b}) => a.localeCompare(b),
    desc: ({regionName: a}, {regionName: b}) => b.localeCompare(a),
  },
  country: {
    asc: ({countryName: a}, {countryName: b}) => a.localeCompare(b),
    desc: ({countryName: a}, {countryName: b}) => b.localeCompare(a),
  },
  countryCode: {
    asc: ({countryCode: a}, {countryCode: b}) => a.localeCompare(b),
    desc: ({countryCode: a}, {countryCode: b}) => b.localeCompare(a),
  },
  type: {
    asc: ({type: a}, {type: b}) => a.localeCompare(b),
    desc: ({type: a}, {type: b}) => b.localeCompare(a),
  },
  deliveryTime: {
    asc: ({deliveryTimeFast: a}, {deliveryTimeFast: b}) => a - b,
    desc: ({deliveryTimeSlow: a}, {deliveryTimeSlow: b}) => b - a,
  },
  price: {
    asc: ({price: a}, {price: b}) => getPriceForSorting(a) - getPriceForSorting(b),
    desc: ({price: a}, {price: b}) => getPriceForSorting(b) - getPriceForSorting(a),
  },
}

type ShippingRowProps = {
  shipping: TableRowContent
  isSelected: boolean
  onToggleSelected: () => void
  onChangeAvailability: (value: boolean) => void
}

const ShippingRow = React.memo(
  ({shipping, isSelected, onToggleSelected, onChangeAvailability, ...props}: ShippingRowProps) => {
    const renderPrice = (price: Shipping['price']) => {
      if (isNil(price)) {
        return '-'
      }

      if (isSinglePrice(price)) {
        return formatPrice(price, shipping.currency)
      }

      return (
        <StaggeredValues
          values={price.map(({weight, price: thisPrice}) => ({
            left: formatPrice(thisPrice, shipping.currency),
            right: `${weight} kg`,
          }))}
        />
      )
    }

    return (
      <TableRow {...props}>
        <TableCell>
          <ActionBar>
            <CheckboxField checked={isSelected} onChange={onToggleSelected} />
          </ActionBar>
        </TableCell>
        <TableCell strong>{shipping.name}</TableCell>
        <TableCell>{shipping.regionName}</TableCell>
        <TableCell>{shipping.countryName}</TableCell>
        <TableCell>{shipping.countryCode}</TableCell>
        <TableCell>{capitalize(shipping.type)}</TableCell>
        <TableCell>
          {formatTimeRange(shipping.deliveryTimeFast, shipping.deliveryTimeSlow)}
        </TableCell>
        <TableCell strong>{renderPrice(shipping.price)}</TableCell>
        <TableCell>
          <ToggleField emphasized checked={shipping.available} onChange={onChangeAvailability} />
        </TableCell>
      </TableRow>
    )
  },
  (prevProps, nextProps) =>
    prevProps.shipping === nextProps.shipping && prevProps.isSelected === nextProps.isSelected
)

ShippingRow.displayName = 'ShippingRow'

const ShippingsPage = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  const countries = useAppSelector(state => state.core.countries)

  invariant(provider, 'Provider is missing.')

  const {
    isLoading,
    data: shippings = [],
    refresh,
  } = useAsync(() => shippingApi.getByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
    defaultLoading: true,
  })

  const {filters, setFilter, resetFilters} =
    useTableRowFilters<ShippingTableRowContent>(initialFilters)

  const [regionFilterOptions, countryFilterOptions] = React.useMemo(() => {
    const countryItems = sortBy(Object.values(countries), 'name')
    return [
      uniq(countryItems.map(country => country.region))
        .filter(region => !!region)
        .sort()
        .map(region => ({
          id: region,
          label: region,
          searchable: region,
        })),
      countryItems.map(country => ({
        id: country.code,
        label: country.name,
        searchable: country.name,
      })),
    ]
  }, [countries])

  const [shippingsById, tableRows] = React.useMemo(() => {
    const shippingTableRows = shippings.map(shipping => ({
      ...shipping,
      regionName: countries[shipping.countryCode].region || '',
      countryName: countries[shipping.countryCode].name || '',
      typeName: capitalize(shipping.type),
    }))

    const sortedShippingTableRows = sortBy(shippingTableRows, ['regionName', 'countryName'])

    return [keyBy(shippings, shipping => shipping.id), sortedShippingTableRows]
  }, [shippings])

  const {searchResults, setSearchValue, searchValue} = useSearch(tableRows, createShippingSearch)

  const filteredTableRows = React.useMemo(
    () => filterTableRows(tableRows, Object.values(filters)),
    [filters, tableRows]
  )

  const searchedAndFilteredTableRows = React.useMemo(
    () => intersection(searchResults, filteredTableRows),
    [searchResults, filteredTableRows]
  )

  const {
    sortedItems: sortedTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(searchedAndFilteredTableRows, SORT_ATTRIBUTES, 'name')

  const {toggleId, toggleAll, removeIds, isSelected, isAnySelected, selectedIds} =
    useSelectedIds<ShippingId>(() => sortedTableRows.map(shipping => shipping.id))

  const refreshAndDeselect = () => {
    refresh()
    removeIds(selectedIds)
  }

  const handleAvailableToggleUpdate = async (checked: boolean, shippingIds: ShippingId[]) => {
    try {
      await shippingApi.patchMultiple(
        {
          available: checked,
        },
        shippingIds
      )
    } catch (error) {
      fatalError({error: error as Error})
    }

    refresh()
  }

  const handleAvailableToggleChange = (checked: boolean, shippingIds: ShippingId[]) => {
    if (shippingIds.length > 1) {
      dispatch(
        openConfirmToggleAvailabilityModal({
          enable: checked,
          onConfirm: () => handleAvailableToggleUpdate(checked, shippingIds),
        })
      )
    } else {
      handleAvailableToggleUpdate(checked, shippingIds)
    }
  }

  const availableSelectedShippingIds = selectedIds.filter(id => shippingsById[id].available)

  // Necessary because of React.memo in the table row.
  // Otherwise updates would work on outdated state.
  const toggleIdRef = React.useRef(toggleId)
  toggleIdRef.current = toggleId
  const handleAvailableToggleChangeRef = React.useRef(handleAvailableToggleChange)
  handleAvailableToggleChangeRef.current = handleAvailableToggleChange

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={<Headline size="l" label="Shipping" />}
            actions={
              <Button
                onClick={() => dispatch(openCreateShippingsModal({onDone: refreshAndDeselect}))}
              >
                Create new option
              </Button>
            }
          />
        }
      >
        <Container>
          <ActionHeaderLayout
            filterBar={
              <FilterBar
                filtersLabel="Filters:"
                filters={
                  <>
                    <StringMatchingFilterPartial
                      size="90"
                      label="Region"
                      options={regionFilterOptions}
                      filterKey="region"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('region', newFilter)}
                    />
                    <StringMatchingFilterPartial
                      size="90"
                      label="Country"
                      options={countryFilterOptions}
                      filterKey="country"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('country', newFilter)}
                      showSearch
                    />
                    <StringMatchingFilterPartial
                      size="90"
                      label="Type"
                      options={shippingFilterOptions}
                      filterKey="type"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('type', newFilter)}
                    />
                    <RangeMatchingFilterPartial
                      size="90"
                      label="Delivery time"
                      filterKey="deliveryTime"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('deliveryTime', newFilter)}
                    />
                  </>
                }
                searchBar={
                  <SearchInputField
                    size="90"
                    name="shippings-search"
                    placeholder="Search by name…"
                    value={searchValue}
                    onChange={value => {
                      setSearchValue(value)
                    }}
                  />
                }
              />
            }
            header={
              <ActionHeader
                left={
                  <LinkButton onClick={() => toggleAll()}>
                    {isAnySelected() ? 'Deselect all' : 'Select all'}
                  </LinkButton>
                }
                right={
                  <>
                    <LinkButton
                      size="200"
                      icon={<Icon source={editIcon} title="Edit" />}
                      disabled={selectedIds.length === 0}
                      onClick={() =>
                        dispatch(
                          openEditShippingsModal({
                            shippings: tableRows.filter(({id}) => selectedIds.includes(id)),
                            onDone: refreshAndDeselect,
                          })
                        )
                      }
                    />
                    <LinkButton
                      size="200"
                      icon={<Icon source={deleteIcon} title="Delete" />}
                      disabled={selectedIds.length === 0}
                      onClick={() => {
                        dispatch(
                          openConfirmDeleteModal({
                            itemsToDeleteCount: selectedIds.length,
                            onConfirm: () => {
                              shippingApi
                                .removeMultiple(selectedIds)
                                .catch(fatalError)
                                .then(refresh)
                            },
                          })
                        )
                      }}
                    />
                    <ToggleField
                      className="u-margin-right"
                      disabled={!isAnySelected()}
                      checked={!!availableSelectedShippingIds.length}
                      onChange={value => handleAvailableToggleChange(value, selectedIds)}
                    />
                  </>
                }
              />
            }
          >
            <Table
              isLoading={isLoading}
              empty={
                tableRows.length === 0 ? (
                  'No shipping options have been added yet.'
                ) : (
                  <Stack>
                    <div>No shippings found</div>
                    <LinkButton
                      onClick={() => {
                        resetFilters()
                        setSearchValue('')
                      }}
                    >
                      Clear all filters
                    </LinkButton>
                  </Stack>
                )
              }
              header={
                <TableRow>
                  <TableHeaderCell width="28px" align="right" />
                  <TableHeaderCell
                    width={4}
                    sort={sortAttribute === 'name' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('name')}
                  >
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    sort={sortAttribute === 'region' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('region')}
                  >
                    Region
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    sort={sortAttribute === 'country' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('country')}
                  >
                    Country
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'countryCode' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('countryCode')}
                  >
                    Country code
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'type' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('type')}
                  >
                    Type
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={3}
                    sort={sortAttribute === 'deliveryTime' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('deliveryTime')}
                  >
                    Delivery time
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    align="right"
                    sort={sortAttribute === 'price' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('price')}
                  >
                    Price
                  </TableHeaderCell>
                  <TableHeaderCell width="40px" align="right">
                    Live
                  </TableHeaderCell>
                </TableRow>
              }
            >
              {sortedTableRows.map(shipping => (
                <ShippingRow
                  key={shipping.id}
                  shipping={shipping}
                  isSelected={isSelected(shipping.id)}
                  onToggleSelected={() => toggleIdRef.current(shipping.id)}
                  onChangeAvailability={value =>
                    handleAvailableToggleChangeRef.current(value, [shipping.id])
                  }
                />
              ))}
            </Table>
          </ActionHeaderLayout>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default ShippingsPage
